import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { Box, Chip, Link, Skeleton, Tooltip, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import {
  getAnnotationToolAnnotatorQueuePath,
  useAppSelector,
  STATUS_BACKGROUND_MAPPING,
  getStatusCode,
  type StatusText,
} from '@verantos-dev/core-annotation-tool';
import { MAX_VISIT_OCC_ID_LENGTH_TO_SHOW } from '@verantos-dev/core-annotation-tool';
import { selectProjectId } from '@verantos-dev/feature-annotator-queue';
import { Breadcrumbs, Button } from '@verantos-dev/shared-components';

import { useAnnotationContent } from './useAnnotationContent';
import { STATUS_CHIP_BACKGROUND_MAPPING, STATUS_CHIP_TEXT_COLOR_MAPPING } from '../../constants';
import { NoteSection } from '../NoteSection/NoteSection';

export const AnnotationContent = () => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const { encounterData, isEncounterDataLoading, selectedNote, concepts, annotations, taskStatus } =
    useAnnotationContent();
  const projectId = useAppSelector(selectProjectId);
  return (
    <Box
      sx={{
        flex: 1,
        p: theme.spacing(6),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(6),
        height: 'calc(100vh - 51px)',
        maxHeight: 'calc(100vh - 51px)',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
        <Breadcrumbs id="annotation-content-breadcrumbs" sx={{ fontSize: '16px' }} separator="/">
          <Link
            color="inherit"
            underline="hover"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate(getAnnotationToolAnnotatorQueuePath());
            }}
            data-testid="breadcrumb-annotation-content"
            sx={{ cursor: 'pointer' }}
          >
            {formatMessage({ id: 'annotationContent.breadcrumb.annotatorQueue' })}
          </Link>
          <Link
            color="inherit"
            underline="none"
            data-testid="breadcrumb-current"
            sx={{ fontWeight: '500' }}
          >
            {formatMessage({ id: 'annotationContent.breadcrumb.annotation' })}
          </Link>
        </Breadcrumbs>

        {isEncounterDataLoading ? (
          <Box>
            <Skeleton variant="text" width="100%" height="100px" />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(4) }}>
                {(() => {
                  const id = encounterData?.visitOccurrenceId ?? '';
                  const truncated = id.length > MAX_VISIT_OCC_ID_LENGTH_TO_SHOW;
                  const display = truncated
                    ? `${id.slice(0, MAX_VISIT_OCC_ID_LENGTH_TO_SHOW)}...`
                    : id;
                  return (
                    <Tooltip title={id} disableHoverListener={!truncated}>
                      <Typography variant="h4">{display}</Typography>
                    </Tooltip>
                  );
                })()}
                <Chip
                  variant="filled"
                  sx={{
                    backgroundColor:
                      STATUS_CHIP_BACKGROUND_MAPPING(theme)[
                        taskStatus?.toLocaleLowerCase() as keyof typeof STATUS_CHIP_BACKGROUND_MAPPING
                      ],
                    color:
                      STATUS_CHIP_TEXT_COLOR_MAPPING(theme)[
                        taskStatus?.toLocaleLowerCase() as keyof typeof STATUS_CHIP_TEXT_COLOR_MAPPING
                      ],
                  }}
                  label={formatMessage({
                    id: `annotationContent.noteSection.statusChip.${taskStatus?.toLocaleLowerCase()}`,
                  })}
                />
              </Box>
              {!selectedNote && (
                // Placeholder button when no note is selected to express that annotation is possible on this screen
                <Button
                  id="annotation-start-annotation-button"
                  displayLabel={formatMessage({
                    id: 'annotationContent.noteSection.startAnnotationButton',
                  })}
                  variant="contained"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    fontSize: '15px',
                    textTransform: 'none',
                  }}
                  disabled={selectedNote == null}
                  onClickHandler={() => {
                    /* noop */
                  }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: theme.spacing(3),
                flexWrap: 'wrap',
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing(1),
                }}
              >
                <BadgeOutlinedIcon sx={{ fontSize: '14px' }} />
                {[
                  encounterData?.patient?.personId,
                  encounterData?.patient?.sex,
                  encounterData?.patient?.age,
                ]
                  .filter(Boolean)
                  .join(' • ')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                <LocalHospitalOutlinedIcon sx={{ fontSize: '14px' }} />
                {encounterData?.encounterType && (
                  <Box
                    sx={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '100%',
                      backgroundColor:
                        STATUS_BACKGROUND_MAPPING[
                          getStatusCode(encounterData?.encounterType as StatusText)
                        ]?.backgroundColor,
                    }}
                  ></Box>
                )}
                <Typography variant="body2" color="text.secondary">
                  {formatMessage({
                    id: `workspaceCommon.content.table.encounterType.${encounterData?.encounterType.toLocaleLowerCase()}`,
                  })}
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing(1),
                }}
              >
                <ScheduleOutlinedIcon sx={{ fontSize: '14px' }} />
                {dayjs(encounterData?.encounterStart).format('MM/DD/YYYY')} —{' '}
                {dayjs(encounterData?.encounterEnd).format('MM/DD/YYYY | HH:mm')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing(1),
                }}
              >
                <ApartmentOutlinedIcon sx={{ fontSize: '14px' }} />
                {encounterData?.projectId}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Box
        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {isEncounterDataLoading ? (
          <Box>
            <Skeleton variant="text" width="100%" height="500px" />
          </Box>
        ) : (
          <NoteSection selectedNote={selectedNote} concepts={concepts} annotations={annotations} />
        )}
      </Box>
    </Box>
  );
};
